import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import clubPlaceholder from '@sorare/core/src/assets/club/shield_none.png';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  DisplayS,
  HeadlineXL,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { BaseLayout } from 'components/rivals/Onboarding/FavoriteClubSelection/ClubSelectionDialog/BaseLayout';
import vs from 'components/rivals/Onboarding/FavoriteClubSelection/ClubSelectionDialog/assets/vs.svg';

import { LearnTheBasics_footballRivalsGame } from './__generated__/index.graphql';

const LeftAlignedVertical = styled(Vertical)`
  align-items: flex-start;
`;
const CompetitionNameWrapper = styled(Horizontal).attrs({ center: true })`
  border-radius: var(--double-unit);
  padding: var(--half-unit) var(--unit);
  background: rgba(var(--c-rgb-neutral-1000), 0.17);
`;
const TeamImage = styled.img`
  object-fit: contain;
  max-width: 100px;
  max-height: 100px;
`;

type Props = {
  color?: string | null;
  rivalsGame?: LearnTheBasics_footballRivalsGame | null;
};
export const LearnTheBasics = ({ rivalsGame, color }: Props) => {
  const { homeTeam, awayTeam, competition, date } = rivalsGame?.game || {};

  return (
    <BaseLayout
      color={color}
      title={
        <FormattedMessage
          id="LearnTheBasics.title"
          defaultMessage="Learn the basics"
        />
      }
      subtitle={
        <FormattedMessage
          id="LearnTheBasics.subtitle"
          defaultMessage="Let’s simulate the last match of your favorite club to learn how to play."
        />
      }
      content={
        <Horizontal center gap={2}>
          <TeamImage src={homeTeam?.pictureUrl || clubPlaceholder} alt="" />
          <img src={vs} alt="" />
          <TeamImage src={awayTeam?.pictureUrl || clubPlaceholder} alt="" />
        </Horizontal>
      }
      footer={
        <LeftAlignedVertical gap={2}>
          {competition?.name && (
            <CompetitionNameWrapper>
              <LabelS color="var(--c-neutral-1000)">{competition.name}</LabelS>
            </CompetitionNameWrapper>
          )}
          <LeftAlignedVertical gap={0}>
            <DisplayS className="text-center">
              {homeTeam?.shortName} <FormattedMessage {...fantasy.vs} />
            </DisplayS>
            <HeadlineXL className="text-center">
              {awayTeam?.shortName}
            </HeadlineXL>
          </LeftAlignedVertical>
          <LabelL color="var(--c-neutral-600)">
            <FormattedDate
              value={date}
              day="numeric"
              month="short"
              year="numeric"
            />
          </LabelL>
        </LeftAlignedVertical>
      }
    />
  );
};

LearnTheBasics.fragments = {
  footballRivalsGame: gql`
    fragment LearnTheBasics_footballRivalsGame on FootballRivalsGame {
      slug
      id
      game {
        id
        date
        competition {
          slug
          name
        }
        homeTeam {
          slug
          shortName
          pictureUrl
        }
        awayTeam {
          slug
          shortName
          pictureUrl
        }
      }
    }
  ` as TypedDocumentNode<LearnTheBasics_footballRivalsGame>,
};
